import React from 'react';
import { Link } from 'react-router-dom';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import SEO from '@/components/layout/SEO';
import { useAuth } from '@/context/AuthContext';

const accessDeniedSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '403 - Access Denied',
    description: 'Access to this resource is restricted on startzone.dev.',
});

export default function AccessDeniedPage() {
    const { user } = useAuth();
    const safeZoneTarget = user ? '/dashboard' : '/';

    return (
        <div className="relative flex min-h-[calc(100vh-4rem)] flex-col overflow-hidden bg-[#0e0e0e] text-white">
            <SEO schema={accessDeniedSchema} title="403 — ACCESS_DENIED" />

            <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.28]" style={{ backgroundImage: 'linear-gradient(#222 1px, transparent 1px), linear-gradient(90deg, #222 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
                <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'radial-gradient(circle, #484848 0.7px, transparent 0.7px)', backgroundSize: '30px 30px' }} />
                <div className="absolute left-1/2 top-[38%] h-[52rem] w-[52rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,113,108,0.18),transparent_72%)] blur-[140px]" />
                <div className="absolute right-[-10rem] top-16 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(circle,rgba(140,172,255,0.12),transparent_72%)] blur-[120px]" />
                <div className="absolute left-[-8rem] bottom-0 h-[24rem] w-[24rem] rounded-full bg-[radial-gradient(circle,rgba(0,207,252,0.08),transparent_72%)] blur-[120px]" />
            </div>
            <div
                className="pointer-events-none fixed inset-0 z-0 opacity-[0.2]"
                style={{
                    background:
                        'linear-gradient(to bottom, rgba(18,16,16,0) 50%, rgba(255,113,108,0.06) 50%), linear-gradient(90deg, rgba(255,0,0,0.03), rgba(0,255,255,0.01), rgba(0,0,255,0.02))',
                    backgroundSize: '100% 3px, 3px 100%',
                }}
            />

            <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
                <div className="absolute left-8 top-24 hidden xl:block">
                    <div className="mb-2 h-px w-24 bg-[#ff716c]/30" />
                    <div className="font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.28em] text-[#ff716c]/70">
                        Barrier_Alpha_9
                    </div>
                </div>
                <div className="absolute right-8 top-24 hidden xl:block font-mono text-[9px] uppercase tracking-[0.28em] text-[#8cacff]/40">
                    PACKET_LOSS: 0.001%
                </div>
                <div className="absolute bottom-10 right-8 hidden xl:block text-right">
                    <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.24em] text-[#767575]/60">
                        COORD: 34.0522° N, 118.2437° W
                    </div>
                    <div className="h-px w-48 bg-[#767575]/25" />
                </div>
                <div className="absolute left-6 top-20 h-12 w-12 border-l border-t border-[#ff716c]/35" />
                <div className="absolute right-6 top-20 h-12 w-12 border-r border-t border-[#484848]/35" />
                <div className="absolute bottom-6 left-6 h-12 w-12 border-b border-l border-[#484848]/35" />
                <div className="absolute bottom-6 right-6 h-12 w-12 border-b border-r border-[#ff716c]/35" />
            </div>

            <main className="relative z-10 flex flex-1 flex-col px-6 pb-20 pt-12 md:px-8 md:pt-16 md:pb-24">
                <div className="mx-auto flex w-full max-w-[58rem] flex-1 items-center justify-center">
                    <div className="w-full max-w-5xl">
                        <div className="mb-8 flex flex-col items-center text-center">
                            <div className="mb-6 font-mono text-[10px] uppercase tracking-[0.36em] text-[#ff716c]">
                                SECURITY_LOCKOUT_DETECTED
                            </div>

                            <div className="relative mb-8">
                                <div className="absolute inset-0 rounded-full bg-[#ff716c]/20 blur-3xl" />
                                <div className="relative flex h-24 w-24 items-center justify-center bg-[#131313] shadow-[0_0_32px_rgba(255,113,108,0.18)] before:absolute before:inset-0 before:bg-[linear-gradient(to_bottom,transparent_50%,rgba(255,113,108,0.06)_50%)] before:bg-[length:100%_3px] before:content-['']">
                                    <MaterialIcon icon="enhanced_encryption" size={56} fill className="relative z-10 text-[#ff716c]" />
                                </div>
                            </div>

                            <div className="mb-8 flex items-center justify-center gap-4">
                                <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#ff716c]/40" />
                                <div className="h-2 w-2 bg-[#ff716c]" />
                                <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#ff716c]/40" />
                            </div>

                            <h1 className="max-w-4xl font-['Space_Grotesk'] text-5xl font-black italic uppercase leading-[0.88] tracking-[-0.06em] text-white md:text-7xl xl:text-[5.75rem]">
                                <span className="inline-block whitespace-nowrap">
                                    ACCESS_DENIED <span className="text-[#ff716c] tracking-normal">//</span>{' '}
                                    <span className="text-[#626262]">403:</span>
                                </span>
                                <br />
                                <span className="text-white">ENCRYPTION_BARRIER</span>
                            </h1>
                        </div>

                        <div className="mx-auto grid w-full max-w-3xl grid-cols-1 overflow-hidden bg-[#141010]/85 shadow-[0_32px_80px_rgba(0,0,0,0.45)] backdrop-blur-sm md:grid-cols-[0.72fr_1.28fr]">
                            <div className="flex flex-col justify-between bg-[#151111] px-6 py-7 md:px-7">
                                <div>
                                    <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#767575]">
                                        STATUS
                                    </div>
                                    <div className="font-['Space_Grotesk'] text-xl font-black uppercase tracking-tight text-[#ff716c]">
                                        LOCKED
                                    </div>
                                </div>
                                <div className="mt-10">
                                    <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#767575]">
                                        ZONE
                                    </div>
                                    <div className="font-['Space_Grotesk'] text-lg font-black uppercase tracking-tight text-white">
                                        CYBER_ZONE_S04
                                    </div>
                                </div>
                            </div>

                            <div className="relative bg-[#1d1818]/80 px-6 py-7 md:px-8">
                                <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'linear-gradient(to bottom, transparent 50%, rgba(255,113,108,0.05) 50%)', backgroundSize: '100% 4px' }} />
                                <div className="relative">
                                    <div className="mb-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#ff716c]">
                                        <MaterialIcon icon="warning" size={16} className="text-current" />
                                        CRITICAL_WARNING
                                    </div>
                                    <p className="border-l border-[#ff716c]/35 pl-4 font-['Inter'] text-lg leading-relaxed text-[#acabaa] md:text-[1.15rem]">
                                        Your current clearance level is insufficient for this sector. Encryption keys
                                        mismatched. All neural-link requests have been terminated to prevent data
                                        contamination.
                                    </p>
                                    <div className="mt-8 space-y-2">
                                        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-[#767575]">
                                            <span>ENCRYPTION_STRENGTH</span>
                                            <span>99.9%</span>
                                        </div>
                                        <div className="h-1 w-full bg-[#252626]">
                                            <div className="h-full w-full bg-[#ff716c] shadow-[0_0_12px_rgba(255,113,108,0.65)]" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mx-auto mt-10 flex w-full max-w-3xl flex-col gap-4 md:flex-row">
                            <Link
                                to={safeZoneTarget}
                                className="group inline-flex w-full items-center justify-center gap-3 bg-[linear-gradient(135deg,#8cacff,#769dff)] p-1 pr-4 text-[#001f55] shadow-[0_0_22px_rgba(140,172,255,0.28)] transition-[filter,background-color,transform] duration-200 hover:brightness-110 active:scale-[0.985] md:w-1/2"
                            >
                                <span className="flex items-center gap-3 px-4 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.22em]">
                                    RETURN TO SAFE ZONE
                                </span>
                                <MaterialIcon icon="chevron_right" size={18} className="text-current transition-transform group-hover:translate-x-0.5" />
                            </Link>
                            <Link
                                to="/contact"
                                className="group flex w-full items-center justify-center gap-3 bg-[#131313] px-8 py-5 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.18em] text-[#9d9d9d] transition-all hover:bg-[#181818] hover:text-[#00cffc] active:scale-[0.985] md:w-1/2"
                            >
                                <MaterialIcon icon="admin_panel_settings" size={18} className="text-current transition-colors" />
                                REQUEST_ELEVATION
                            </Link>
                        </div>

                        <div className="mx-auto mt-14 w-full max-w-3xl overflow-hidden border-t border-[#1d1f21] pt-6 font-mono text-[10px] uppercase tracking-tight text-[#767575]/45 opacity-70">
                            <div className="flex gap-4 whitespace-nowrap">
                                <span>[SYS_LOG] 0x403 forbidden: user_id_3301 mismatch</span>
                                <span>[SYS_LOG] trace_route_blocked_by_proxy</span>
                                <span>[SYS_LOG] integrity_check_failed</span>
                                <span>[SYS_LOG] reporting_to_central_nodes</span>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
